import type { GlobalStorage } from "@otto-ec/global-resources";
import type { HistorySearch, HistorySearchItem } from "../types/suggestion";

import * as protobufHistory from "@otto-ec/squirrel_protobuf-history";
import { INTERACTION, STORAGE_ITEM, TRACK_LABEL } from "../types/constants";
import { default as B } from "buffer";
import { CookieBanner, DresonMap } from "../helper";
import { FTFindDresonResolvedEvent } from "../types/ftfind";
import { DRESON_PREFIX, FILTER_NAME, SortingMap } from "../types/dreson/constants";

export class SubmitHistory {
  private _searches: HistorySearchItem[];
  private readonly _maxTotalLength: number;
  private readonly _maxViewedLength: number;

  constructor(maxTotalLength: number, maxViewedLength: number) {
    this._searches = CookieBanner.isEnabled() ? this.readSearchesFromLocalStorage() : [];
    this._maxTotalLength = maxTotalLength;
    this._maxViewedLength = maxViewedLength > maxTotalLength ? maxTotalLength : maxViewedLength;
  }

  get maxTotalLength(): number {
    return this._maxTotalLength;
  }

  get maxViewedLength(): number {
    return this._maxViewedLength;
  }

  toSeconds(ms: number): number {
    if (ms <= 0) {
      return ms;
    }
    return Math.floor(ms / 1000);
  }

  encode(): string {
    const history: HistorySearch = protobufHistory.createHistory(
      this._searches.map((search: HistorySearchItem): protobufHistory.protoHistory.History.HistoryItem => {
        search.date = this.toSeconds(search.date);
        return search as protobufHistory.protoHistory.History.HistoryItem;
      }) as protobufHistory.protoHistory.History.HistoryItem[],
      this.toSeconds(Date.now()),
    );
    const binary: Uint8Array = history.serializeBinary();
    return B.Buffer.from(binary).toString("base64");
  }

  isEmpty(): boolean {
    return this.currentLength() <= 0;
  }

  retrieveAllSearches(): HistorySearchItem[] {
    return CookieBanner.isEnabled() ? this.readSearchesFromLocalStorage() : this._searches;
  }

  currentLength(): number {
    return this._searches.length;
  }

  addSearch(term: string, position?: number) {
    if (term == "" || !term) {
      return;
    }
    const term_lower: string = term.toLowerCase();

    const date: number = new Date().getTime();
    let newItem: HistorySearchItem;
    if (position !== undefined && position !== null) {
      newItem = { ...this._searches[position] };
      newItem.date = date;
    } else {
      newItem = {
        term: term_lower,
        date,
      };
    }
    const duplicateEntry = this.currentlyLastItemInHistory();
    if (duplicateEntry !== -1) {
      this.deleteSearch(duplicateEntry);
    }
    this._searches = [newItem, ...this._searches].slice(0, this._maxTotalLength);

    this.writeSearchesToLocalStorage();
  }

  deleteAllSearches() {
    const positions: number[] = [...this._searches.keys()];
    this.deleteSearches(positions);
  }

  deleteSearch(position: number) {
    this._searches = this._searches.filter((_, i: number) => i !== position);
    this.writeSearchesToLocalStorage();
  }

  deleteSearches(positions: number[]) {
    this._searches = this._searches.filter((_, i: number) => !positions.includes(i));
    this.writeSearchesToLocalStorage();
  }

  hasFilter(position: number): boolean {
    if (position == undefined) {
      return false;
    }
    const dresonMap = this.retrieveAllSearches()[position].dresonMap;
    return !!dresonMap && dresonMap!.getAllFilterNames.length > 0;
  }

  filterExists(): boolean {
    return this.retrieveAllSearches().some(
      (item: HistorySearchItem): boolean => !!item.dresonMap && item.dresonMap.getAllFilterNames.length > 0,
    );
  }

  getSearchPosition(term: string): number {
    return this._searches.findIndex((search: HistorySearchItem): boolean => search.term === term);
  }

  getStorage(): GlobalStorage {
    return new window.o_global.Storage(() => window.localStorage);
  }

  updateFilterAndSortingOnLatestItem(event: FTFindDresonResolvedEvent) {
    const dresonMap = new DresonMap();
    dresonMap.setRule(event.rule);
    if (event.redirect) {
      this.updateFilterOnLatestItem(dresonMap, event.redirect);
    } else if (event.tsLink && event.tsLink[TRACK_LABEL.INTERACTION] === INTERACTION.SORT_CHANGE) {
      this.updateSortingOnLatestItem(dresonMap, event.tsLink[TRACK_LABEL.SORTING_CHANGE]);
    }
  }

  private static removeDuplicatesInHistory(historySearchItems: HistorySearchItem[]): HistorySearchItem[] {
    return [...historySearchItems]
      .reverse()
      .reduce((uniqueHistoryItems: HistorySearchItem[], item: HistorySearchItem) => {
        if (!uniqueHistoryItems.some((historyItem) => historyItem.term == item.term)) {
          uniqueHistoryItems.push(item);
        }
        return uniqueHistoryItems;
      }, [])
      .reverse();
  }

  private currentlyLastItemInHistory(): number {
    const lastItem = this._searches[0];
    return this._searches.findIndex((item: HistorySearchItem, index: number) => {
      if (index === 0) {
        return;
      }
      if (lastItem.term === item.term) {
        return (
          !(lastItem.dresonMap || item.dresonMap) ||
          (lastItem.dresonMap && item.dresonMap && lastItem.dresonMap.equals(item.dresonMap))
        );
      }
    });
  }

  private readSearchesFromLocalStorage(): HistorySearchItem[] {
    const storage: GlobalStorage = this.getStorage();
    const searchesString: string | null = storage.getItem(STORAGE_ITEM.HISTORICAL_DATA);
    if (searchesString) {
      const parsed = JSON.parse(searchesString);
      return parsed
        .map((item: HistorySearchItem) => {
          const dresonMap = item.dresonMap ? new DresonMap(Object.entries(item.dresonMap)) : item.dresonMap;
          return {
            ...item,
            dresonMap,
          };
        })
        .sort((currentItem: HistorySearchItem, nextItem: HistorySearchItem) => {
          if (currentItem.date > nextItem.date) {
            return -1;
          } else if (currentItem.date < nextItem.date) {
            return 1;
          } else {
            return 0;
          }
        });
    }
    return [];
  }

  private writeSearchesToLocalStorage() {
    if (CookieBanner.isEnabled()) {
      const storage: GlobalStorage = this.getStorage();
      const searchesToStringify = this._searches.map((item) => {
        let newDresonMap;
        if (item.dresonMap) {
          newDresonMap = Object.fromEntries(item.dresonMap);
        }
        return {
          ...item,
          dresonMap: newDresonMap,
        };
      });
      const searchesString: string = JSON.stringify(searchesToStringify);
      storage.setItem(STORAGE_ITEM.HISTORICAL_DATA, searchesString);
    }
  }

  private updateFilterOnLatestItem(dresonMap: DresonMap, redirect: string) {
    dresonMap.set("_redirect", window.location.origin + redirect);
    if (redirect.includes(FILTER_NAME.SORTIERT_NACH)) {
      const url = new URL(redirect, window.location.origin);
      dresonMap.set(
        FILTER_NAME.SORTIERT_NACH,
        SortingMap.get(<string>url.searchParams.get(FILTER_NAME.SORTIERT_NACH)) as string,
      );
    }
    const dresonMapSearchTerm = dresonMap.get(DRESON_PREFIX.SUCHBEGRIFF) as string;
    if (dresonMapSearchTerm === this._searches[0]?.term) {
      this._searches[0].dresonMap = dresonMap;
      this.writeSearchesToLocalStorage();
    }
  }

  private updateSortingOnLatestItem(dresonMap: DresonMap, san_SortingChange: string) {
    const dresonMapSearchTerm = dresonMap.get(DRESON_PREFIX.SUCHBEGRIFF) as string;
    if (dresonMapSearchTerm === this._searches[0]?.term) {
      const currentRedirect =
        (this._searches[0].dresonMap?.get(FILTER_NAME.REDIRECT) as string) ?? `/suche/${dresonMapSearchTerm}`;
      const url = new URL(currentRedirect, window.location.origin);
      san_SortingChange && url.searchParams.set(FILTER_NAME.SORTIERT_NACH, san_SortingChange);
      dresonMap.set(FILTER_NAME.REDIRECT, url.href);
      dresonMap.set(FILTER_NAME.SORTIERT_NACH, SortingMap.get(san_SortingChange) as string);
      this._searches[0].dresonMap = dresonMap;
      this.writeSearchesToLocalStorage();
    }
  }
}

const instance: SubmitHistory = new SubmitHistory(10, 5);
export const submitHistory = {
  addSearch: instance.addSearch.bind(instance),
  encode: instance.encode.bind(instance),
  retrieveAllSearch: instance.retrieveAllSearches.bind(instance),
  deleteAllSearches: instance.deleteAllSearches.bind(instance),
  deleteSearch: instance.deleteSearch.bind(instance),
  deleteSearches: instance.deleteSearches.bind(instance),
  isEmpty: instance.isEmpty.bind(instance),
  getSearchPosition: instance.getSearchPosition.bind(instance),
  updateFilterAndSortingOnLatestItem: instance.updateFilterAndSortingOnLatestItem.bind(instance),
  filterExists: instance.filterExists.bind(instance),
  hasFilter: instance.hasFilter.bind(instance),
};
